import React, { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import { useMeetingStaffContext } from 'components/meetings/MeetingPage/MeetingStaffView/MeetingStaffContext';
import MeetingService from 'services/MeetingService';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import PatentedTechnologyCard from './PatentedTechnologyCard';
import { TechnologyConflict } from 'store/types/Meeting';
import { ActivityType, IPCheckType, QuestionResponse, TechnologyType } from 'store/enums/BallotReviewTypes';
import { defaultSnackbarErrorProps } from 'util/layout';
import { meetingSessionToActivity } from 'util/meeting';

import commonStyles from 'styles/common.module.scss';

const inputWidth = 3;
const patentMotionId = 'patent-check-motion';

const PatentedSection: React.FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();
  const [numberPatented, setNumberPatented] = useState(
    meetingSession?.currentIpCheckSession?.patentSummary?.totalConflicts ?? ''
  );
  const [numberPatentedAfter, setNumberPatentedAfter] = useState(
    meetingSession?.currentIpCheckSession?.patentSummary?.conflictsAfterIssuance ?? ''
  );
  const [numberPatentedBefore, setNumberPatentedBefore] = useState(
    meetingSession?.currentIpCheckSession?.patentSummary?.conflictsBeforeIssuance ?? ''
  );
  const [patentedItems, setPatentedItems] = useState<TechnologyConflict[]>([]);

  const hasPatentSummary = numberPatented !== '' || numberPatentedAfter !== '' || numberPatentedBefore !== '';
  const hasPatentedTechnologies =
    Number(numberPatentedBefore) > 0 || Number(numberPatented) > 0 || Number(numberPatentedAfter) > 0;

  const savePatentSummaryProgress = useCallback(
    async (summary: { totalConflicts: string; conflictsBeforeIssuance: string; conflictsAfterIssuance: string }) => {
      try {
        await MeetingService.saveMeetingSession({
          ...meetingSession,
          meetingId,
          currentIpCheckSession: {
            ...meetingSession?.currentIpCheckSession,
            type: IPCheckType.Affirmative,
            patentConflict: QuestionResponse.Yes,
            patentSummary: summary,
          },
        });
      } catch (error) {
        console.error('Error saving patented technology summary progress:', error);
      }
    },
    [meetingId, meetingSession]
  );

  const handleSave = useCallback(async () => {
    logActivity(
      {
        ...meetingSessionToActivity(meetingSession),
        type: ActivityType.IpCheck,
        ipCheckSession: {
          type: IPCheckType.Affirmative,
          patentConflict: QuestionResponse.Yes,
          patentSummary: {
            totalConflicts: numberPatented,
            conflictsBeforeIssuance: numberPatentedBefore,
            conflictsAfterIssuance: numberPatentedAfter,
          },
        },
      },
      'Saved total numbers of Patented Technologies'
    );
  }, [logActivity, meetingSession, numberPatented, numberPatentedAfter, numberPatentedBefore]);

  const addItem = useCallback(
    () =>
      setPatentedItems([
        ...patentedItems,
        {
          id: `patented-item-${Date.now()}`,
          type: TechnologyType.Patent,
          description: '',
          dateOfAssessment: null,
        },
      ]),
    [patentedItems]
  );

  const updateItem = useCallback((id: string, patch: Partial<TechnologyConflict>) => {
    setPatentedItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }, []);

  const removeItem = useCallback(
    (id: string) => setPatentedItems(patentedItems.filter((i) => id !== i.id)),
    [patentedItems]
  );

  const handleDisplayPatent = useCallback(
    async (patent: TechnologyConflict) => {
      try {
        await MeetingService.saveMeetingSession({
          ...meetingSession,
          meetingId,
          currentIpCheckSession: undefined,
          currentTechnologyConflict: patent,
        });
      } catch (error) {
        enqueueSnackbar(`Failed to diplay patent`, defaultSnackbarErrorProps);
        console.error('Error displaying patent:', error);
      }
    },
    [enqueueSnackbar, meetingId, meetingSession]
  );

  const handleClearPatent = useCallback(async () => {
    try {
      await MeetingService.clearVotes(meetingId, patentMotionId);
      await MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentTechnologyConflict: undefined,
        currentMotion: meetingSession?.currentMotion?.id === patentMotionId ? undefined : meetingSession?.currentMotion,
        currentVotingSession:
          meetingSession?.currentVotingSession?.motionId === patentMotionId
            ? undefined
            : meetingSession?.currentVotingSession,
        currentApprovalLevel:
          meetingSession?.currentMotion?.id === patentMotionId ? undefined : meetingSession?.currentApprovalLevel,
      });

      enqueueSnackbar(`Cleared patent`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to clear patent`, defaultSnackbarErrorProps);
      console.error('Error clearing patent:', error);
    }
  }, [enqueueSnackbar, meetingId, meetingSession]);

  return (
    <GridContainer>
      <GridItem>
        <Typography variant="h6">Patented Technologies Subsection</Typography>
      </GridItem>
      <GridItem>
        <GridContainer spacing={1}>
          <GridItem>
            <Typography variant="subtitle2">Total numbers of Patented Technologies to be dealt with</Typography>
          </GridItem>
          <GridItem md={inputWidth}>
            <TextField
              label={'Fill Number'}
              value={numberPatented}
              type="number"
              onChange={(event) => {
                const value = event.target.value;
                setNumberPatented(value);
                void savePatentSummaryProgress({
                  totalConflicts: value,
                  conflictsBeforeIssuance: numberPatentedBefore,
                  conflictsAfterIssuance: numberPatentedAfter,
                });
              }}
            ></TextField>
          </GridItem>
          <GridItem md={12 - inputWidth} alignContent={'center'}>
            <Typography>Known Patented Technology that might be relevant to the Standard/Safety Guideline</Typography>
          </GridItem>
          <GridItem md={inputWidth}>
            <TextField
              label={'Fill Number'}
              value={numberPatentedAfter}
              type="number"
              onChange={(event) => {
                const value = event.target.value;
                setNumberPatentedAfter(value);
                void savePatentSummaryProgress({
                  totalConflicts: numberPatented,
                  conflictsBeforeIssuance: numberPatentedBefore,
                  conflictsAfterIssuance: value,
                });
              }}
            ></TextField>
          </GridItem>
          <GridItem md={12 - inputWidth} alignContent={'center'}>
            <Typography>
              Number of patented technologies first became known to the TC Chapter on or after the day of the issuance
              of this Letter Ballot{' '}
              <span className={commonStyles.redText}>
                (Postpone assessment of such patented technologies to be performed at the next scheduled TC Chapter
                meeting.)
              </span>
            </Typography>
          </GridItem>
          <GridItem md={inputWidth}>
            <TextField
              label={'Fill Number'}
              value={numberPatentedBefore}
              type="number"
              onChange={(event) => {
                const value = event.target.value;
                setNumberPatentedBefore(value);
                void savePatentSummaryProgress({
                  totalConflicts: numberPatented,
                  conflictsBeforeIssuance: value,
                  conflictsAfterIssuance: numberPatentedAfter,
                });
              }}
            ></TextField>
          </GridItem>
          <GridItem md={12 - inputWidth} alignContent={'center'}>
            <Typography>
              Number of patented technologies first became known to the TC Chapter before the day of the issuance of
              this Letter Ballot
            </Typography>
          </GridItem>
          {hasPatentSummary && (
            <GridItem>
              <Typography>
                <strong>Result:</strong>{' '}
                {hasPatentedTechnologies
                  ? 'Go to Assessment of disclosed patented technologies'
                  : 'Go to Assessment of disclosed copyrighted items'}
              </Typography>
            </GridItem>
          )}
        </GridContainer>
      </GridItem>
      <GridItem>
        <Button variant="contained" color="secondary" onClick={handleSave}>
          Save
        </Button>
      </GridItem>
      {hasPatentedTechnologies && (
        <>
          <GridItem>
            <Typography variant="h6">Assessment of disclosed patented technologies</Typography>
          </GridItem>
          {patentedItems.map((item, index) => (
            <GridItem key={`${item.id}-card`}>
              <PatentedTechnologyCard
                item={item}
                index={index}
                updateItem={updateItem}
                removeItem={removeItem}
                displayConflict={handleDisplayPatent}
                clearConflict={handleClearPatent}
              />
            </GridItem>
          ))}
          <GridItem>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={addItem}>
              Add Patented Technology
            </Button>
          </GridItem>
        </>
      )}
    </GridContainer>
  );
};

export default PatentedSection;
